import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Wrench, Loader2, Cog, Car, Settings, Gauge, Disc3, Bolt, type LucideIcon } from 'lucide-react';
import { useAuth } from './useAuth';

const schema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});
type FormValues = z.infer<typeof schema>;

// Icônes décoratives « garage » dispersées en fond (hors de la carte centrale).
const DECOR: { Icon: LucideIcon; top: string; left: string; size: number; rotate: number; cls: string }[] = [
  { Icon: Cog, top: '6%', left: '8%', size: 150, rotate: -12, cls: 'text-primary/[0.07]' },
  { Icon: Wrench, top: '16%', left: '84%', size: 96, rotate: 22, cls: 'text-foreground/[0.05]' },
  { Icon: Car, top: '72%', left: '9%', size: 120, rotate: -8, cls: 'text-primary/[0.06]' },
  { Icon: Settings, top: '76%', left: '82%', size: 140, rotate: 14, cls: 'text-foreground/[0.05]' },
  { Icon: Gauge, top: '44%', left: '4%', size: 76, rotate: 10, cls: 'text-foreground/[0.05]' },
  { Icon: Disc3, top: '30%', left: '93%', size: 64, rotate: -18, cls: 'text-primary/[0.07]' },
  { Icon: Bolt, top: '58%', left: '90%', size: 52, rotate: 8, cls: 'text-foreground/[0.06]' },
  { Icon: Wrench, top: '90%', left: '46%', size: 72, rotate: -24, cls: 'text-primary/[0.06]' },
  { Icon: Cog, top: '4%', left: '58%', size: 68, rotate: 18, cls: 'text-foreground/[0.05]' },
];

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  // Déjà connecté → rediriger.
  if (status === 'authenticated') return <Navigate to={from} replace />;

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        setServerError('Identifiants invalides.');
      } else if (err instanceof AxiosError && err.response?.status === 429) {
        setServerError('Trop de tentatives. Réessayez dans quelques minutes.');
      } else {
        setServerError('Une erreur est survenue. Réessayez.');
      }
    }
  };

  const fieldClass =
    'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm shadow-sm outline-none transition-[border-color,box-shadow] focus:border-ring focus:ring-2 focus:ring-ring/25';

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-muted/40 p-4">
      {/* Halo doux derrière la carte */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" aria-hidden />

      {/* Icônes garage dispersées en fond */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {DECOR.map((d, i) => (
          <d.Icon
            key={i}
            className={`absolute ${d.cls}`}
            width={d.size}
            height={d.size}
            strokeWidth={1.5}
            style={{ top: d.top, left: d.left, transform: `translate(-50%, -50%) rotate(${d.rotate}deg)` }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
            <Wrench className="h-6 w-6" />
          </span>
          <h1 className="text-xl font-semibold">Gestion Salihina</h1>
          <p className="text-sm text-muted-foreground">Accédez à votre espace de gestion.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium">
              Email
            </label>
            <input id="email" type="email" autoComplete="username" className={fieldClass} {...register('email')} />
            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium">
              Mot de passe
            </label>
            <input id="password" type="password" autoComplete="current-password" className={fieldClass} {...register('password')} />
            {errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          {serverError && (
            <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400">
              {serverError}
            </p>
          )}

          <div className="flex justify-center pt-1">
            <button
              type="submit"
              disabled={isSubmitting}
              className="mx-auto flex w-fit items-center justify-center gap-2 rounded-full bg-primary px-10 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Se connecter
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
